import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Calendar = ({ 
  calendarData = {}, 
  onDateClick, 
  className = "" 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Get calendar data for the current month
  const getMonthData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDay = new Date(startDate);
    
    while (currentDay <= lastDay || days.length < 42) {
      const dateString = currentDay.toISOString().split('T')[0];
      const dayData = calendarData[dateString] || { status: 'N' };
      
      days.push({
        date: new Date(currentDay),
        dateString,
        status: dayData.status,
        setId: dayData.setId,
        score: dayData.score,
        isCurrentMonth: currentDay.getMonth() === month
      });
      
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DS': return 'bg-green-500'; // Done with success
      case 'DF': return 'bg-red-500';   // Done but failed
      case 'N': return 'bg-gray-300';   // Not yet studied
      default: return 'bg-gray-300';
    }
  };

  const getStatusTooltip = (status) => {
    switch (status) {
      case 'DS': return 'Done with success';
      case 'DF': return 'Done but test failed';
      case 'N': return 'Not yet studied';
      default: return 'Not yet studied';
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const monthData = getMonthData();
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className={`bg-white rounded-xl shadow-sm ${className}`}>
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-800">
            {formatDate(currentDate)}
          </h3>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Today
          </button>
        </div>
        
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {monthData.map((day, index) => (
            <div
              key={index}
              onClick={() => onDateClick && onDateClick(day)}
              className={`
                relative p-2 text-center cursor-pointer rounded-lg transition-colors
                ${day.isCurrentMonth ? 'hover:bg-gray-50' : 'text-gray-400'}
                ${day.dateString === today ? 'bg-blue-100' : ''}
              `}
            >
              <div className="text-sm font-medium">
                {day.date.getDate()}
              </div>
              
              {/* Status indicator */}
              <div
                className={`
                  absolute bottom-1 left-1/2 transform -translate-x-1/2
                  w-2 h-2 rounded-full ${getStatusColor(day.status)}
                `}
                title={getStatusTooltip(day.status)}
              />
              
              {/* Score indicator for completed days */}
              {day.status !== 'N' && day.score && (
                <div className="absolute top-1 right-1 text-xs text-gray-500">
                  {day.score}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t bg-gray-50">
        <div className="flex items-center justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Done Successfully</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Done with Errors</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            <span>Not Studied</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
